import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type MouseEvent as ReactMouseEvent,
} from "react";
import { Link } from "react-router-dom";
import {
  CalendarDays,
  Check,
  ChevronLeft,
  ChevronRight,
  LayoutGrid,
  List,
  ListChecks,
  UserX,
  Video,
} from "lucide-react";
import { OutcomeModal } from "@/components/prospects/OutcomeModal";
import { PageHeader } from "@/components/layout/PageHeader";
import { api } from "@/lib/api";
import { useToast } from "@/components/ui/Toast";
import { cn, fullName, localISO, todayISO } from "@/lib/utils";
import { TONE_STYLES } from "@/lib/colors";
import { initialCalendarView } from "@/lib/mobile-defaults";
import { getRdvState, getRdvTone, getRdvLabel, hasStarted } from "@/lib/rdv";
import { annulerPointage, pointer } from "@/lib/pointage";
import type { RdvOutcome } from "@shared/schema";
import type { GoogleEvent, Prospect } from "@shared/schema";

type EnrichedGoogleEvent = GoogleEvent & { prospect: Prospect | null };
type View = "month" | "agenda";
const VIEW_KEY = "pccrm.calendar.view";

interface DayBucket {
  iso: string;
  date: Date;
  rdv: EnrichedGoogleEvent[];
}

export function CalendarPage() {
  const [events, setEvents] = useState<EnrichedGoogleEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState<View>(() =>
    initialCalendarView(
      localStorage.getItem(VIEW_KEY),
      window.matchMedia("(max-width: 639px)").matches,
    ),
  );
  const [cursor, setCursor] = useState(() => {
    const d = new Date();
    return new Date(d.getFullYear(), d.getMonth(), 1);
  });
  const toast = useToast();

  // On n'enregistre que le choix explicite : écrire au montage figerait la
  // vue par défaut (liste sur téléphone) en « choix de l'utilisateur ».
  function chooseView(v: View) {
    setView(v);
    localStorage.setItem(VIEW_KEY, v);
  }

  const load = useCallback(() => {
    return api
      .googleEvents({ limit: 200 })
      .then((e) => {
        setEvents(e.events.filter((ev) => ev.status === "confirmed"));
      })
      .catch((err) =>
        toast.error(err instanceof Error ? err.message : "Erreur"),
      )
      .finally(() => setLoading(false));
  }, [toast]);

  useEffect(() => {
    load();
  }, [load]);

  const eventsByDay = useMemo(() => {
    const m = new Map<string, EnrichedGoogleEvent[]>();
    for (const e of events) {
      const day = localISO(new Date(e.startTime));
      if (!m.has(day)) m.set(day, []);
      m.get(day)!.push(e);
    }
    for (const list of m.values()) {
      list.sort((a, b) => (a.startTime < b.startTime ? -1 : 1));
    }
    return m;
  }, [events]);

  return (
    <>
      <PageHeader
        title="Calendrier"
        subtitle="Rendez-vous bookés via Calendly"
        actions={
          <div className="inline-flex rounded-[var(--radius-md)] border border-[var(--color-border-strong)] p-0.5 bg-[var(--color-surface)]">
            <button
              type="button"
              onClick={() => chooseView("month")}
              className={cn(
                "btn btn-ghost btn-sm",
                view === "month" && "bg-[var(--color-muted)]",
              )}
              aria-pressed={view === "month"}
            >
              <LayoutGrid size={14} /> Agenda
            </button>
            <button
              type="button"
              onClick={() => chooseView("agenda")}
              className={cn(
                "btn btn-ghost btn-sm",
                view === "agenda" && "bg-[var(--color-muted)]",
              )}
              aria-pressed={view === "agenda"}
            >
              <List size={14} /> À venir
            </button>
          </div>
        }
      />

      {loading ? (
        <div className="surface p-8 text-center text-sm text-[var(--color-muted-foreground)]">
          Chargement…
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-4 lg:gap-6">
          {view === "month" ? (
            <MonthView
              cursor={cursor}
              setCursor={setCursor}
              eventsByDay={eventsByDay}
            />
          ) : (
            <AgendaView eventsByDay={eventsByDay} onChange={load} />
          )}

          <aside className="space-y-4">
            <section className="surface p-4">
              <h3 className="text-sm font-semibold text-[var(--color-navy-700)] mb-3">
                Légende
              </h3>
              <ul className="space-y-2 text-xs">
                <li className="flex items-center gap-2">
                  <span
                    className={cn(
                      "h-3 w-6 rounded border-l-2",
                      TONE_STYLES.sky.pillBg,
                      TONE_STYLES.sky.border,
                    )}
                  />
                  RDV à venir
                </li>
                <li className="flex items-center gap-2">
                  <span
                    className={cn(
                      "h-3 w-6 rounded border-l-2",
                      TONE_STYLES.yellow.pillBg,
                      TONE_STYLES.yellow.border,
                    )}
                  />
                  Présent
                </li>
                <li className="flex items-center gap-2">
                  <span
                    className={cn(
                      "h-3 w-6 rounded border-l-2",
                      TONE_STYLES.slate.pillBg,
                      TONE_STYLES.slate.border,
                    )}
                  />
                  Absent
                </li>
                <li className="flex items-center gap-2">
                  <span
                    className={cn(
                      "h-3 w-6 rounded border-l-2",
                      TONE_STYLES.coral.pillBg,
                      TONE_STYLES.coral.border,
                    )}
                  />
                  À pointer
                </li>
                <li className="flex items-center gap-2">
                  <span
                    className={cn(
                      "h-3 w-6 rounded border-l-2",
                      TONE_STYLES.green.pillBg,
                      TONE_STYLES.green.border,
                    )}
                  />
                  Client gagné
                </li>
                <li className="flex items-center gap-2">
                  <span
                    className={cn(
                      "h-3 w-6 rounded border-l-2",
                      TONE_STYLES.red.pillBg,
                      TONE_STYLES.red.border,
                    )}
                  />
                  Perdu
                </li>
                <li className="flex items-center gap-2">
                  <span className="h-3 w-3 rounded-full bg-[var(--color-coral-500)]" />
                  Aujourd'hui
                </li>
              </ul>
            </section>
          </aside>
        </div>
      )}
    </>
  );
}

function MonthView({
  cursor,
  setCursor,
  eventsByDay,
}: {
  cursor: Date;
  setCursor: (d: Date) => void;
  eventsByDay: Map<string, EnrichedGoogleEvent[]>;
}) {
  const monthCells = useMemo(() => {
    const year = cursor.getFullYear();
    const month = cursor.getMonth();
    const firstDay = new Date(year, month, 1);
    const startDow = (firstDay.getDay() + 6) % 7;
    const daysInMonth = new Date(year, month + 1, 0).getDate();

    const cells: Array<{
      date: Date;
      iso: string;
      inMonth: boolean;
      rdv: EnrichedGoogleEvent[];
    }> = [];

    const prev = new Date(year, month, -startDow + 1);
    for (let i = 0; i < 42; i++) {
      const d = new Date(prev);
      d.setDate(prev.getDate() + i);
      const iso = localISO(d);
      cells.push({
        date: d,
        iso,
        inMonth: d.getMonth() === month,
        rdv: eventsByDay.get(iso) ?? [],
      });
    }
    return cells.slice(0, Math.ceil((startDow + daysInMonth) / 7) * 7);
  }, [cursor, eventsByDay]);

  return (
    <section className="surface p-4 lg:p-5">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold text-[var(--color-navy-700)] capitalize">
          {cursor.toLocaleDateString("fr-FR", {
            month: "long",
            year: "numeric",
          })}
        </h2>
        <div className="flex items-center gap-1">
          <button
            type="button"
            className="btn btn-ghost btn-icon btn-sm"
            onClick={() =>
              setCursor(new Date(cursor.getFullYear(), cursor.getMonth() - 1, 1))
            }
            aria-label="Mois précédent"
          >
            <ChevronLeft size={16} />
          </button>
          <button
            type="button"
            className="btn btn-ghost btn-sm"
            onClick={() => {
              const now = new Date();
              setCursor(new Date(now.getFullYear(), now.getMonth(), 1));
            }}
          >
            Aujourd'hui
          </button>
          <button
            type="button"
            className="btn btn-ghost btn-icon btn-sm"
            onClick={() =>
              setCursor(new Date(cursor.getFullYear(), cursor.getMonth() + 1, 1))
            }
            aria-label="Mois suivant"
          >
            <ChevronRight size={16} />
          </button>
        </div>
      </div>
      <div className="grid grid-cols-7 gap-px bg-[var(--color-border)] rounded-[var(--radius-md)] overflow-hidden">
        {["Lun", "Mar", "Mer", "Jeu", "Ven", "Sam", "Dim"].map((d) => (
          <div
            key={d}
            className="bg-[var(--color-surface-2)] text-xs font-medium text-[var(--color-muted-foreground)] py-2 text-center"
          >
            {d}
          </div>
        ))}
        {monthCells.map((cell, idx) => {
          const isToday = cell.iso === todayISO();
          const totalEntries = cell.rdv.length;
          return (
            <div
              key={idx}
              className={cn(
                "bg-white min-h-24 lg:min-h-28 p-1.5 lg:p-2 flex flex-col gap-1",
                !cell.inMonth && "opacity-40",
              )}
            >
              <div
                className={cn(
                  "text-xs font-medium tabular self-end h-6 w-6 grid place-items-center rounded-full",
                  isToday
                    ? "bg-[var(--color-coral-500)] text-white"
                    : "text-[var(--color-muted-foreground)]",
                )}
              >
                {cell.date.getDate()}
              </div>
              <div className="flex flex-col gap-1 overflow-hidden">
                {cell.rdv.slice(0, 3).map((e) => {
                  const time = new Date(e.startTime).toLocaleTimeString("fr-FR", {
                    hour: "2-digit",
                    minute: "2-digit",
                  });
                  const name = e.prospect
                    ? fullName(e.prospect)
                    : (e.attendeeName ?? e.attendeeEmail ?? "RDV");
                  const href = e.prospect ? `/prospects/${e.prospect.id}` : "/calendrier";
                  const tone = TONE_STYLES[getRdvTone(e, e.prospect?.status)];
                  return (
                    <Link
                      key={`r-${e.id}`}
                      to={href}
                      className={cn(
                        "text-xs px-1.5 py-0.5 rounded truncate font-medium border-l-2",
                        tone.pillBg,
                        tone.pillFg,
                        tone.border,
                        tone.cardHover,
                      )}
                      title={`${time} · ${name} · ${e.summary ?? ""}`}
                    >
                      <span className="tabular text-[0.65rem] opacity-70 mr-1">{time}</span>
                      {name}
                    </Link>
                  );
                })}
                {totalEntries > 3 && (
                  <div className="text-xs text-[var(--color-muted-foreground)] px-1.5">
                    +{totalEntries - 3} autres
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}

function AgendaView({
  eventsByDay,
  onChange,
}: {
  eventsByDay: Map<string, EnrichedGoogleEvent[]>;
  onChange: () => void;
}) {
  const [daysAhead, setDaysAhead] = useState(60);
  // "À venir" starts today: only upcoming RDVs. Past days (for marking
  // Présent / Absent) are loaded on demand via "Voir 30 jours avant".
  const [daysBack, setDaysBack] = useState(0);

  const buckets = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const result: DayBucket[] = [];
    for (let i = -daysBack; i < daysAhead; i++) {
      const d = new Date(today);
      d.setDate(today.getDate() + i);
      const iso = localISO(d);
      const rdv = eventsByDay.get(iso) ?? [];
      if (rdv.length === 0) continue;
      result.push({ iso, date: d, rdv });
    }
    return result;
  }, [eventsByDay, daysAhead, daysBack]);

  if (buckets.length === 0) {
    return (
      <section className="surface p-8 text-center">
        <CalendarDays
          size={32}
          className="mx-auto text-[var(--color-muted-foreground)] mb-3"
        />
        <p className="text-sm text-[var(--color-muted-foreground)]">
          Rien de prévu dans les {daysAhead} prochains jours.
        </p>
        <button
          type="button"
          className="btn btn-outline btn-sm mt-4"
          onClick={() => setDaysAhead((d) => d + 30)}
        >
          Étendre à {daysAhead + 30} jours
        </button>
      </section>
    );
  }

  return (
    <section className="surface p-0 overflow-hidden">
      <ul>
        {buckets.map((bucket) => (
          <AgendaDayRow key={bucket.iso} bucket={bucket} onChange={onChange} />
        ))}
      </ul>
      <div className="border-t border-[var(--color-border)] p-3 flex flex-wrap justify-between gap-2">
        <button
          type="button"
          className="btn btn-ghost btn-sm"
          onClick={() => setDaysBack((d) => d + 30)}
        >
          ← Voir 30 jours avant
        </button>
        <button
          type="button"
          className="btn btn-ghost btn-sm"
          onClick={() => setDaysAhead((d) => d + 30)}
        >
          Voir 30 jours après →
        </button>
      </div>
    </section>
  );
}

function AgendaDayRow({
  bucket,
  onChange,
}: {
  bucket: DayBucket;
  onChange: () => void;
}) {
  const isToday = bucket.iso === todayISO();
  const isTomorrow =
    bucket.iso ===
    localISO(new Date(new Date().getTime() + 24 * 60 * 60 * 1000));
  const weekday = bucket.date.toLocaleDateString("fr-FR", { weekday: "long" });
  const dayMonth = bucket.date.toLocaleDateString("fr-FR", {
    day: "numeric",
    month: "long",
  });
  return (
    <li className="grid grid-cols-[140px_1fr] max-sm:grid-cols-1 max-sm:gap-2 max-sm:px-4 lg:grid-cols-[180px_1fr] gap-4 px-5 py-4 border-b border-[var(--color-border)] last:border-b-0 hover:bg-[var(--color-surface-2)] transition-colors">
      <div className="flex flex-col">
        <div
          className={cn(
            "text-xs uppercase tracking-wide font-medium",
            isToday
              ? "text-[var(--color-coral-600)]"
              : "text-[var(--color-muted-foreground)]",
          )}
        >
          {isToday ? "Aujourd'hui" : isTomorrow ? "Demain" : weekday}
        </div>
        <div
          className={cn(
            "text-lg font-semibold tabular capitalize",
            isToday
              ? "text-[var(--color-coral-600)]"
              : "text-[var(--color-navy-700)]",
          )}
        >
          {dayMonth}
        </div>
      </div>
      <div className="flex flex-col gap-2 min-w-0">
        {bucket.rdv.map((e) => (
          <AgendaRdvCard key={`r-${e.id}`} event={e} onChange={onChange} />
        ))}
      </div>
    </li>
  );
}

function AgendaRdvCard({
  event,
  onChange,
}: {
  event: EnrichedGoogleEvent;
  onChange: () => void;
}) {
  const toast = useToast();
  const [busy, setBusy] = useState(false);
  const [issueOpen, setIssueOpen] = useState(false);
  const time = new Date(event.startTime).toLocaleTimeString("fr-FR", {
    hour: "2-digit",
    minute: "2-digit",
  });
  const endTime = event.endTime
    ? new Date(event.endTime).toLocaleTimeString("fr-FR", {
        hour: "2-digit",
        minute: "2-digit",
      })
    : null;
  const name = event.prospect
    ? fullName(event.prospect)
    : (event.attendeeName ?? event.attendeeEmail ?? "RDV");
  const href = event.prospect
    ? `/prospects/${event.prospect.id}`
    : "/calendrier";
  const state = getRdvState(event);
  const tone = TONE_STYLES[getRdvTone(event, event.prospect?.status)];
  const label = getRdvLabel(event, event.prospect?.status);
  const started = hasStarted(event, new Date());

  async function mark(e: ReactMouseEvent, outcome: RdvOutcome) {
    e.preventDefault();
    e.stopPropagation();
    setBusy(true);
    try {
      // Toggle off if the same value is clicked again
      if (event.outcome === outcome) {
        if (event.prospect) await annulerPointage(event.id);
        else await api.setEventOutcome(event.id, null);
      } else if (event.prospect) {
        await pointer(
          event.prospect.id,
          event.id,
          outcome === "attended" ? "present" : "no_show",
        );
      } else {
        await api.setEventOutcome(event.id, outcome);
      }
      onChange();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Erreur");
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <Link
        to={href}
        className={cn(
          "flex items-stretch gap-3 rounded-[var(--radius-md)] border transition-colors group",
          tone.cardBorder,
          tone.cardBg,
          tone.cardHover,
        )}
      >
        <div className={cn("w-1.5 rounded-l-[var(--radius-md)]", tone.dot)} />
        <div className="flex-1 min-w-0 py-2.5 pr-3">
          <div className="flex items-center gap-2 mb-0.5">
            <span
              className={cn(
                "text-xs font-semibold uppercase tracking-wide",
                tone.pillFg,
              )}
            >
              {label}
            </span>
            <span
              className={cn("text-xs tabular font-medium opacity-80", tone.pillFg)}
            >
              {time}
              {endTime && ` → ${endTime}`}
            </span>
            {event.meetingUrl && state !== "cancelled" && (
              <a
                href={event.meetingUrl}
                target="_blank"
                rel="noreferrer"
                onClick={(e) => e.stopPropagation()}
                className={cn(
                  "ml-auto hover:text-[var(--color-coral-600)]",
                  tone.pillFg,
                )}
                aria-label="Lien visio"
              >
                <Video size={14} />
              </a>
            )}
          </div>
          <div
            className={cn(
              "font-semibold truncate group-hover:text-[var(--color-coral-600)]",
              state === "cancelled"
                ? "text-[var(--color-muted-foreground)] line-through"
                : "text-[var(--color-navy-700)]",
            )}
          >
            {name}
          </div>
          {event.summary && (
            <div className="text-xs text-[var(--color-muted-foreground)] truncate">
              {event.summary}
            </div>
          )}
          {started && state !== "cancelled" && (
            <div className="flex flex-wrap items-center gap-2 mt-2">
              <button
                type="button"
                disabled={busy}
                onClick={(e) => mark(e, "attended")}
                className={cn(
                  "inline-flex items-center gap-1 text-xs font-medium px-2 py-1 max-sm:min-h-11 max-sm:px-3 max-sm:whitespace-nowrap rounded-[var(--radius-sm)] border transition-colors",
                  event.outcome === "attended"
                    ? "bg-[color-mix(in_srgb,#16a34a_14%,white)] text-[#0f6b30] border-[color-mix(in_srgb,#16a34a_35%,white)]"
                    : "bg-white text-[var(--color-muted-foreground)] border-[var(--color-border-strong)] hover:bg-[var(--color-muted)]",
                )}
              >
                <Check size={13} /> Présent
              </button>
              <button
                type="button"
                disabled={busy}
                onClick={(e) => mark(e, "no_show")}
                className={cn(
                  "inline-flex items-center gap-1 text-xs font-medium px-2 py-1 max-sm:min-h-11 max-sm:px-3 max-sm:whitespace-nowrap rounded-[var(--radius-sm)] border transition-colors",
                  event.outcome === "no_show"
                    ? "bg-[color-mix(in_srgb,#64748b_16%,white)] text-[#475569] border-[color-mix(in_srgb,#64748b_40%,white)]"
                    : "bg-white text-[var(--color-muted-foreground)] border-[var(--color-border-strong)] hover:bg-[var(--color-muted)]",
                )}
              >
                <UserX size={13} /> Absent
              </button>
              {event.prospect && (
                <button
                  type="button"
                  disabled={busy}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setIssueOpen(true);
                  }}
                  className="inline-flex items-center gap-1 text-xs font-medium px-2 py-1 max-sm:min-h-11 max-sm:px-3 max-sm:whitespace-nowrap rounded-[var(--radius-sm)] border transition-colors bg-white text-[var(--color-navy-700)] border-[var(--color-border-strong)] hover:bg-[var(--color-muted)]"
                >
                  <ListChecks size={13} /> Issue
                </button>
              )}
            </div>
          )}
        </div>
      </Link>
      {issueOpen && event.prospect && (
        <OutcomeModal
          open
          prospect={event.prospect}
          eventId={event.id}
          eventStart={event.startTime}
          onClose={() => setIssueOpen(false)}
          onDone={onChange}
        />
      )}
    </>
  );
}
